import * as React from 'react'
import { Loader2, MoreHorizontal, MoreVertical } from 'lucide-react'
import { Button, type ButtonProps } from '@axilon-apps/libs-ui-shadcn'

interface MenuButtonThreeDotsOwnProps {
  /**
   * Show a spinner instead of the dots glyph. Defaults to false.
   * The button is also disabled while loading.
   */
  loading?: boolean
  /**
   * Disabled state. Forced on while `loading` is true.
   */
  disabled?: boolean
  /**
   * Pick the dots orientation. Defaults to horizontal (•••).
   */
  orientation?: 'horizontal' | 'vertical'
}

export type MenuButtonThreeDotsProps = MenuButtonThreeDotsOwnProps &
  Omit<ButtonProps, keyof MenuButtonThreeDotsOwnProps>

/**
 * Compact icon-button rendering a three-dots glyph. Designed as the trigger
 * for table-row action menus and similar disclosures.
 *
 * Wrapped in React.forwardRef + spreads remaining props so it cooperates with
 * Radix's `asChild` Slot — e.g.
 *
 *   <DropdownMenuTrigger asChild>
 *     <MenuButtonThreeDots />
 *   </DropdownMenuTrigger>
 *
 * Radix injects `onClick`, `onPointerDown`, `onKeyDown`, `aria-haspopup`,
 * `aria-expanded`, `data-state`, and a ref through the Slot. We must pass
 * all of them down to the underlying <Button> (and therefore the real
 * <button>) — otherwise the menu either fails to open or fails to position.
 */
const MenuButtonThreeDots = React.forwardRef<HTMLButtonElement, MenuButtonThreeDotsProps>(
  function MenuButtonThreeDots(
    {
      loading = false,
      disabled = false,
      orientation = 'horizontal',
      variant = 'outline',
      size = 'icon-sm',
      ...props
    },
    ref
  ) {
    const Icon = orientation === 'horizontal' ? MoreHorizontal : MoreVertical

    return (
      <Button
        ref={ref}
        type="button"
        variant={variant}
        size={size}
        disabled={loading || disabled}
        aria-label="Open actions menu"
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" /> : <Icon />}
      </Button>
    )
  }
)

export default MenuButtonThreeDots
